import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertContext";
import { useLoading } from "../../context/LoadingContext";
import { getServiceRequest } from "../../commons/ServiceRequest";
import {
  emptyValueObjects,
  filteredItemByArray
} from "../../utils/ManageArray";
import TableForm from "../TableForm";
import ConfirmModal from "../ConfirmModal";
import SearchFilter from "../../components/SearchFilter";
import ItemsService from "../../services/item.service";
import WarrantiesService from "../../services/warranty.service";
import CardDisplayComponent from "../CardDisplayComponent";

const WarrantyPeriodForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [itemMas, setItemMas] = useState([]);
  const [warranty, setWarranty] = useState([]);
  const defaultData = {
    Item_Group: "",
    Item_Code: "",
    Item_Des: "",
    Serial_No: "",
    Sup_to_FVC: "",
    FVC_to_Sup: "",
    Com_Code: user.Com_Code
  };
  const [data, setData] = useState({ Array: [defaultData] });
  const searchInput = [
    {
      name: "Item_Group",
      label: "กลุ่มสินค้า",
      disabled: !!editData,
      value: editData?.Item_Group || null,
      queryService: (query) => queryItemMaster(query)
    },
    {
      name: "Item_Code",
      label: "รหัสสินค้า",
      disabled: !!editData,
      value: editData?.Item_Code || null,
      queryService: (query) => queryItemMaster(query)
    },
    {
      name: "Item_Name_TH",
      label: "ชื่อสินค้า",
      disabled: !!editData,
      value: editData?.Item_Des || null,
      queryService: (query) => queryItemMaster(query)
    }
  ];
  const inputField = [
    { name: "Item_Code", label: "รหัสสินค้า", readOnly: true, type: "text" },
    {
      name: "Item_Des",
      label: "รายละเอียดสินค้า",
      readOnly: true,
      type: "text"
    },
    {
      name: "Serial_No",
      label: "หมายเลขเครื่อง (Serial No)",
      readOnly: true,
      type: "text"
    },
    {
      name: "Sup_to_FVC",
      label: "อายุประกัน Sup to FVC (เดือน)",
      type: "text",
      pattern: /^(?!0)\d{1,2}$/.source,
      feedbackMessage:
        "ข้อมูลไม่ถูกต้อง โปรดระบุจำนวนเต็ม 2 ตำแหน่ง และต้องที่ค่ามากกว่า 0 (จำนวนเต็มสูงสุด 2 ตัวอักษร)"
    },
    {
      name: "FVC_to_Sup",
      label: "อายุประกัน FVC to Sup (เดือน)",
      type: "text",
      pattern: /^(?!0)\d{1,2}$/.source,
      feedbackMessage:
        "ข้อมูลไม่ถูกต้อง โปรดระบุจำนวนเต็ม 2 ตำแหน่ง และต้องที่ค่ามากกว่า 0 (จำนวนเต็มสูงสุด 2 ตัวอักษร)"
    }
  ];

  const queryItemMaster = (query) => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: ItemsService.getAllItems(query),
      callback: ({ items }) => setItemMas(items)
    });
  };

  const handleSearchFiltered = (search) => {
    const filteredItems = itemMas.filter(
      (item) => !warranty.some((war) => item.Item_Code === war.Item_Code)
    );

    const updatedData = search.some((item) => item.value)
      ? filteredItemByArray(filteredItems, search).map((item, idx) => ({
          Item_Group: filteredItems[idx]?.Item_Group,
          Item_Code: filteredItems[idx]?.Item_Code,
          Item_Des: filteredItems[idx]?.Item_Name_TH,
          Serial_No: filteredItems[idx]?.Serial_No || "",
          Sup_to_FVC: "",
          FVC_to_Sup: "",
          Com_Code: user.Com_Code
        }))
      : [Object.assign({}, defaultData)];

    if (updatedData.length === 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "รหัสสินค้าซ้ำกัน ไม่สามารถถูกนำมาใช้ได้",
        type: "danger"
      });
    }

    setData({
      Array: updatedData.length === 0 ? [defaultData] : updatedData
    });
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (emptyValueObjects(data.Array, ["Serial_No"]).length > 0)
      return showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณาเลือกสินค้าก่อน",
        type: "danger"
      });

    if (!form.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      if (!editData) onSubmitCallback(dataCallback);
      else onSubmitCallback(dataCallback.Array[0]);
    }
  };

  useEffect(() => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: WarrantiesService.getAllWarranties(),
      callback: (res) => setWarranty(res)
    });

    if (editData) {
      queryItemMaster(`?keyword=${editData.Item_Code}`);

      setData({
        Array: [
          {
            Item_Group: editData.Item_Group,
            Item_Code: editData.Item_Code,
            Item_Des: editData.Item_Des,
            Serial_No: editData.Serial_No,
            Sup_to_FVC: editData.Sup_to_FVC,
            FVC_to_Sup: editData.FVC_to_Sup,
            Com_Code: editData.Com_Code
          }
        ]
      });
    }
  }, [editData]);

  return (
    <>
      <CardDisplayComponent
        body={{
          element: (
            <SearchFilter
              data={itemMas}
              inputField={searchInput}
              fieldClassName={{
                actionButton: "w-auto py-2",
                actionDisabled: { start: !!editData, end: !!editData }
              }}
              searchCallback={(searchCB) => handleSearchFiltered(searchCB)}
            />
          )
        }}
      />

      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          body={{
            element: (
              <TableForm
                editForm
                data={data.Array}
                columns={inputField}
                onChangeCallback={(newData) => setData({ Array: newData })}
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
                </Button>
                <ConfirmModal
                  data={data}
                  show={showModal}
                  content={editData ? "edit" : "save"}
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-success"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() => navigate(-1)}
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

WarrantyPeriodForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default WarrantyPeriodForm;
